import { useLocation } from "wouter";
import { Sparkles, ClipboardCheck, ArrowUpRight, Camera } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { CleaningSubmissionSummary } from "@shared/schema";

// Inline timeline card for a member's form submission — a cleaning-assignment
// proof (kind "cleaning") or any other service form they submitted (kind
// "form"). Mirrors ReportedTaskCard's shape: compact, jumpable, interleaved
// chronologically with the chat stream. Cleaning proofs land on the Cleaning
// page's Forms Received queue; plain forms land on the forms admin page.
export function CleaningProofCard({ card, memberName }: {
  card: CleaningSubmissionSummary;
  memberName: string | null;
}) {
  const [, navigate] = useLocation();
  const isCleaning = card.kind === "cleaning";
  const Icon = isCleaning ? Sparkles : ClipboardCheck;
  return (
    <div
      className="my-2 mx-auto max-w-md rounded-lg border border-green-200 bg-green-50 px-3 py-2"
      data-testid={`cleaning-proof-card-${card.submissionId}`}
    >
      <div className="flex items-start gap-2">
        <div className="mt-0.5 h-7 w-7 rounded-full bg-green-100 flex items-center justify-center shrink-0">
          <Icon className="h-3.5 w-3.5 text-green-700" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs text-green-900">
            <span className="font-medium">{memberName ?? "Member"}</span> submitted{" "}
            {isCleaning ? (
              <>
                the {card.templateName ?? "cleaning"} proof
                {card.scheduledDate ? <> for <span className="font-medium">{card.scheduledDate}</span></> : null}
                {card.period === "week" ? " (their week)" : ""}
              </>
            ) : (
              <>the <span className="font-medium">{card.templateName ?? "service"}</span> form</>
            )}
          </p>
          {card.photoCount > 0 && (
            <p className="text-[11px] text-green-700 mt-0.5 flex items-center gap-1">
              <Camera className="h-3 w-3" />
              {card.photoCount} photo{card.photoCount === 1 ? "" : "s"} attached
            </p>
          )}
        </div>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-7 px-2 text-[11px] text-green-800 hover:text-green-900 hover:bg-green-100 shrink-0"
          onClick={() =>
            navigate(
              isCleaning && card.assignmentId != null
                ? `/app/cleaning?tab=received&assignment=${card.assignmentId}`
                : `/app/operations/service-forms?submission=${card.submissionId}`,
            )
          }
          data-testid={`button-view-cleaning-proof-${card.submissionId}`}
        >
          View
          <ArrowUpRight className="h-3 w-3 ml-1" />
        </Button>
      </div>
    </div>
  );
}
